<template>
  <div class="topmost">
    <nav class="bg-gray-800 shadow-lg">
      <div class="max-w-7xl mx-auto px-4">
        <div class="flex justify-between h-14">
          <!-- Left side - Logo/Company Name -->
          <div class="flex items-center">
            <router-link to="/" class="flex items-center">
              <span class="font-bold hover:text-sky-200 " style="color: #ffa500" >
                {{configs.company_name}}
              </span>
            </router-link>
          </div>

          <!-- Mobile menu button -->
          <div class="flex items-center md:hidden">
            <button @click.prevent="show_menu_mobile = !show_menu_mobile"
                    class="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-white hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                    aria-expanded="false">
              <span class="sr-only">Open main menu</span>
              <svg class="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>

          <!-- Desktop Navigation -->
          <div class="hidden md:flex md:items-center md:space-x-4" 
               :class="{'mobile-nav': show_menu_mobile}"
               v-if="user_store.session_check == 1">
            <!-- Search Button -->
            <button v-if="user_store.is_loggedin && am_allowed('menu_roles.can_see_menu_search')"
                    @click.prevent="start_open_search_page()"
                    class="inline-flex items-center px-3 py-2 rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700 transition-colors duration-200">
              <i class="fa-solid fa-magnifying-glass"></i>
            </button>

            <!-- Notifications -->
            <div v-if="user_store.is_loggedin" class="relative" v-click-outside="closeNotifications">
              <button @click.prevent="open_notifications_list()"
                      class="inline-flex items-center px-3 py-2 rounded-md text-sm font-medium text-white bg-sky-600 hover:bg-sky-700 transition-colors duration-200">
                <i class="fa-solid fa-bell" :class="{ 'mr-2': menu_store.notification_count }"></i>
                <span v-if="menu_store.notification_count" class="bg-red-500 text-white text-xs rounded-full px-2 py-0.5">{{ menu_store.notification_count }}</span>
              </button>

              <div v-if="menu_store.show_menu_notifications"
                   class="absolute right-0 mt-2 w-96 max-w-[calc(100vw-2rem)] rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5">
                <div class="flex items-center justify-between border-b border-gray-100 px-4 py-3">
                  <span class="text-sm font-semibold text-gray-800">Njoftime</span>
                  <span v-if="menu_store.notification_count" class="rounded-full bg-sky-50 px-2 py-0.5 text-xs font-medium text-sky-700">
                    {{ menu_store.notification_count }}
                  </span>
                </div>
                <div class="max-h-96 overflow-y-auto">
                  <NotificationList />
                </div>
              </div>
            </div>

            <!-- User Profile & Help -->
            <router-link v-if="user_store.is_loggedin" to="/profile" 
                        class="text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200">
              <i class="fa-solid fa-user mr-2"></i>
              <span>{{user_store.user.name}}</span>
            </router-link>

            <router-link to="/help"
                        class="text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200">
              Help
            </router-link>

            <!-- Login/Logout -->
            <router-link v-if="!user_store.is_loggedin" to="/login"
                        class="text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200">
              Login
            </router-link>

            <button v-if="user_store.is_loggedin" @click.prevent="logout"
                    class="text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200">
              Logout
            </button>
          </div>

          <!-- Loading State -->
          <div v-if="user_store.session_check == -1" class="flex items-center">
            <div class="text-sky-400 px-3 py-2">
              <i class="fa-solid fa-spinner fa-spin text-lg"></i>
            </div>
          </div>
        </div>
      </div>

      <!-- Mobile Navigation Menu -->
      <div v-if="show_menu_mobile" class="md:hidden">
        <div class="px-2 pt-2 pb-3 space-y-1 sm:px-3">
          <!-- Mobile menu items -->
          <router-link v-if="user_store.is_loggedin" to="/profile" 
                      class="text-gray-300 hover:text-white block px-3 py-2 rounded-md text-base font-medium">
            <i class="fa-solid fa-user mr-2"></i>{{user_store.user.name}}
          </router-link>

          <router-link to="/help"
                      class="text-gray-300 hover:text-white block px-3 py-2 rounded-md text-base font-medium">
            Help
          </router-link>

          <button v-if="user_store.is_loggedin" @click.prevent="logout"
                  class="text-gray-300 hover:text-white block w-full text-left px-3 py-2 rounded-md text-base font-medium">
            Logout
          </button>
        </div>
      </div>
    </nav>

    <Cookies v-if="!has_cookies" />
  </div>
</template>

<script>
import Api from '@/services/Api'
import { storeToRefs } from 'pinia'
import { useUserStore } from '@/stores/user'
import { useMenuStore } from '@/stores/menu'
import { defineAsyncComponent } from 'vue'
import { useConfigurationsStore } from '@/stores/configurations'
import permissionMixin from '@/mixins/permissions-mixin.js'
import NotificationList from '@/components/notifications/NotificationList.vue'

	export default{
		mixins: [permissionMixin],
		components: {
			Cookies: defineAsyncComponent( () => import('@/components/Cookies.vue') ),
			NotificationList,
		},
		data(){
			return {
				user_store: storeToRefs(useUserStore()),
				menu_store: storeToRefs(useMenuStore()),
				
				show_menu_mobile: false, 
				configs: storeToRefs(useConfigurationsStore()),
				notificationPollTimer: null,
			}
		},
		computed: {
			has_cookies() {
				return localStorage.cookies;
			},
		},
		watch: {
			"user_store.is_loggedin": {
				immediate: true,
				handler(val) {
					if (val) {
						this.startNotificationPoll();
					} else {
						this.stopNotificationPoll();
						useUserStore().seenPriceChangeIds = null;
						useUserStore().seenOfferChangeIds = null;
						useUserStore().seenIntakeSyncIds = null;
					}
				},
			},
		},
		beforeUnmount() {
			this.stopNotificationPoll();
		},
		created(){
		},
		methods: {
			clearLocalStorage(){
				localStorage.removeItem("user")
				localStorage.removeItem("token")
				localStorage.removeItem("user_roles")
				localStorage.removeItem("permissions")
				// localStorage.removeItem("hiden_columns")
			},
			logout(){
				Api(true).post("user/logout", {})
					.finally(() => {
						this.clearLocalStorage()
						this.$router.push({path: "/login"})
						this.user_store.is_loggedin = false
						this.user_store.user = {}
						useUserStore().seenPriceChangeIds = null
						useUserStore().seenOfferChangeIds = null
						useUserStore().seenIntakeSyncIds = null
						this.stopNotificationPoll()
					})
			},
			open_notifications_list(){
				useMenuStore().open_notifications_list()
			},
			closeNotifications(){
				useMenuStore().show_menu_notifications = false
			},
			start_open_search_page(){
				this.$router.push({name: 'Kerko produkte', path: "/search_page"})
			},
			async pollNotifications() {
				if (!useUserStore().is_loggedin) return;
				try {
					const result = await useUserStore().get_user_notifications();
					const newcomers = result?.newcomers || [];
					newcomers.forEach((n) => {
						const title = n.title
							|| (n.sync_id ? "Erdhi Furnizimi" : null)
							|| (n.guid ? "Oferta u aktivizua" : "Ndryshimi i cmimeve");
						if (n.status === "failed") {
							this.$toast.error(title);
						} else {
							this.$toast.success(title);
						}
					});
				} catch (_) {
					/* ignore poll errors */
				}
			},
			startNotificationPoll() {
				this.stopNotificationPoll();
				this.pollNotifications();
				this.notificationPollTimer = setInterval(() => {
					this.pollNotifications();
				}, 45000);
			},
			stopNotificationPoll() {
				if (this.notificationPollTimer) {
					clearInterval(this.notificationPollTimer);
					this.notificationPollTimer = null;
				}
			},
		}
	}
</script>

<style scoped>

.mobile-menu{
	display: block!important;
	color:#fff!important;
	background: #646f9b;
}
#navbarBasicExample .navbar-end .navbar-item{
	color:#ffffff;
}

.topmost{
	position: relative;
	z-index: 9999;
}
</style>

